import { AdminLayout } from "@/components/layout/AdminLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Search, Loader2, Pencil, Trash2, Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { adminService, SiteVisitRecord } from "../../services/adminService";

type FormState = {
  path: string;
  referrer: string;
  user_agent_hash: string;
  ip_address: string;
  city: string;
  state: string;
  country: string;
  browser_name: string;
  browser_version: string;
  os_name: string;
  os_version: string;
  device_type: string;
  user_agent: string;
};

const emptyForm = (): FormState => ({
  path: "",
  referrer: "",
  user_agent_hash: "",
  ip_address: "",
  city: "",
  state: "",
  country: "",
  browser_name: "",
  browser_version: "",
  os_name: "",
  os_version: "",
  device_type: "",
  user_agent: "",
});

const rowToForm = (r: SiteVisitRecord): FormState => ({
  path: r.path ?? "",
  referrer: r.referrer ?? "",
  user_agent_hash: r.user_agent_hash ?? "",
  ip_address: r.ip_address ?? "",
  city: r.city ?? "",
  state: r.state ?? "",
  country: r.country ?? "",
  browser_name: r.browser_name ?? "",
  browser_version: r.browser_version ?? "",
  os_name: r.os_name ?? "",
  os_version: r.os_version ?? "",
  device_type: r.device_type ?? "",
  user_agent: r.user_agent ?? "",
});

const formatBrowser = (name?: string | null, ver?: string | null) =>
  [name, ver].filter(Boolean).join(" ").trim() || "—";
const formatOs = (name?: string | null, ver?: string | null) =>
  [name, ver].filter(Boolean).join(" ").trim() || "—";

const UserSiteVisits = () => {
  const [rows, setRows] = useState<SiteVisitRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<SiteVisitRecord | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  const fetchRows = useCallback(
    async (page = 1, search = "") => {
      try {
        setLoading(true);
        const data = await adminService.getSiteVisitRecords({
          page,
          page_size: pagination.pageSize,
          search: search || undefined,
          ordering: "-created_at",
        });
        setRows(data.results ?? []);
        setPagination(prev => ({ ...prev, count: data.count, page }));
      } catch {
        toast.error("Failed to load site visits.");
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) {
      return;
    }
    fetchedRef.current = true;
    fetchRows(1, "");
    return () => {
      fetchedRef.current = false;
    };
  }, [fetchRows]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchRows(1, searchTerm);
  };

  const openAdd = () => {
    setForm(emptyForm());
    setAddOpen(true);
  };

  const openEdit = (r: SiteVisitRecord) => {
    setSelected(r);
    setForm(rowToForm(r));
    setEditOpen(true);
  };

  const openDelete = (r: SiteVisitRecord) => {
    setSelected(r);
    setDeleteOpen(true);
  };

  const payloadFromForm = () => ({
    path: form.path.trim(),
    referrer: form.referrer.trim(),
    user_agent_hash: form.user_agent_hash.trim(),
    ip_address: form.ip_address.trim(),
    city: form.city.trim(),
    state: form.state.trim(),
    country: form.country.trim(),
    browser_name: form.browser_name.trim(),
    browser_version: form.browser_version.trim(),
    os_name: form.os_name.trim(),
    os_version: form.os_version.trim(),
    device_type: form.device_type.trim(),
    user_agent: form.user_agent.trim(),
  });

  const handleCreate = async () => {
    try {
      setSaving(true);
      await adminService.createSiteVisitRecord(payloadFromForm());
      toast.success("Visit record created");
      setAddOpen(false);
      fetchRows(pagination.page, searchTerm);
    } catch {
      toast.error("Could not create record");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected) {
      return;
    }
    try {
      setSaving(true);
      await adminService.updateSiteVisitRecord(selected.id, payloadFromForm());
      toast.success("Visit record updated");
      setEditOpen(false);
      setSelected(null);
      fetchRows(pagination.page, searchTerm);
    } catch {
      toast.error("Could not update record");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selected) {
      return;
    }
    try {
      await adminService.deleteSiteVisitRecord(selected.id);
      toast.success("Visit record deleted");
      setDeleteOpen(false);
      setSelected(null);
      fetchRows(pagination.page, searchTerm);
    } catch {
      toast.error("Could not delete record");
    }
  };

  const totalPages = Math.max(
    1,
    Math.ceil(pagination.count / pagination.pageSize)
  );

  return (
    <AdminLayout>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <h2 className="text-xl font-bold text-foreground md:text-3xl">
            User Site Visits
          </h2>
          <div className="flex w-full flex-col gap-2 sm:max-w-2xl sm:flex-row sm:items-center">
            <form
              onSubmit={handleSearch}
              className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center"
            >
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search path, IP, city, country, referrer…"
                  className="w-full pl-9"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                variant="secondary"
                className="shrink-0 bg-[#533a1d] text-white hover:bg-[#533a1d]/90"
              >
                Search
              </Button>
            </form>
            <Button
              type="button"
              className="shrink-0 sm:ml-2"
              onClick={openAdd}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add visit
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="w-14 px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    ID
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Date
                  </th>
                  <th className="min-w-[120px] px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Path
                  </th>
                  <th className="min-w-[100px] max-w-[200px] px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Referrer
                  </th>
                  <th className="max-w-[100px] px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    UA hash
                  </th>
                  <th className="min-w-[110px] px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    IP
                  </th>
                  <th className="px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    City
                  </th>
                  <th className="px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    State
                  </th>
                  <th className="px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Country
                  </th>
                  <th className="min-w-[100px] px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Browser
                  </th>
                  <th className="min-w-[100px] px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    OS
                  </th>
                  <th className="w-20 px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    Device
                  </th>
                  <th className="max-w-[160px] px-3 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                    UA
                  </th>
                  <th className="w-28 px-3 py-3 text-right font-semibold uppercase tracking-wider text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <p>Loading site visits…</p>
                      </div>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={14}
                      className="px-4 py-12 text-center text-muted-foreground"
                    >
                      No site visit records found.
                    </td>
                  </tr>
                ) : (
                  rows.map(item => (
                    <tr
                      key={item.id}
                      className="transition-colors hover:bg-muted/30"
                    >
                      <td className="px-3 py-3 font-medium">{item.id}</td>
                      <td className="whitespace-nowrap px-3 py-3">
                        {new Date(item.created_at).toLocaleString()}
                      </td>
                      <td
                        className="max-w-[200px] truncate px-3 py-3"
                        title={item.path}
                      >
                        {item.path || "—"}
                      </td>
                      <td
                        className="max-w-[200px] truncate px-3 py-3"
                        title={item.referrer}
                      >
                        {item.referrer || "—"}
                      </td>
                      <td
                        className="max-w-[120px] truncate px-3 py-3 font-mono text-xs"
                        title={item.user_agent_hash}
                      >
                        {item.user_agent_hash || "—"}
                      </td>
                      <td className="max-w-[120px] truncate px-3 py-3 font-mono text-xs">
                        {item.ip_address ?? "—"}
                      </td>
                      <td className="max-w-[100px] truncate px-3 py-3">
                        {item.city ?? "—"}
                      </td>
                      <td className="max-w-[80px] truncate px-3 py-3">
                        {item.state ?? "—"}
                      </td>
                      <td className="max-w-[80px] truncate px-3 py-3">
                        {item.country ?? "—"}
                      </td>
                      <td
                        className="max-w-[140px] truncate px-3 py-3 text-xs"
                        title={formatBrowser(
                          item.browser_name,
                          item.browser_version
                        )}
                      >
                        {formatBrowser(item.browser_name, item.browser_version)}
                      </td>
                      <td
                        className="max-w-[140px] truncate px-3 py-3 text-xs"
                        title={formatOs(item.os_name, item.os_version)}
                      >
                        {formatOs(item.os_name, item.os_version)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-xs capitalize">
                        {item.device_type ?? "—"}
                      </td>
                      <td
                        className="max-w-[180px] truncate px-3 py-3 font-mono text-[10px]"
                        title={item.user_agent ?? undefined}
                      >
                        {item.user_agent
                          ? `${item.user_agent.slice(0, 72)}${
                              item.user_agent.length > 72 ? "…" : ""
                            }`
                          : "—"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEdit(item)}
                          aria-label="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => openDelete(item)}
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <div className="text-sm text-muted-foreground">
            {pagination.count > 0 ? (
              <>
                Showing{" "}
                <span className="font-medium text-foreground">
                  {(pagination.page - 1) * pagination.pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium text-foreground">
                  {Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.count
                  )}
                </span>{" "}
                of{" "}
                <span className="font-medium text-foreground">
                  {pagination.count}
                </span>{" "}
                results
              </>
            ) : (
              "No results to show"
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchRows(1, searchTerm)}
              disabled={pagination.page === 1 || loading}
            >
              « First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchRows(pagination.page - 1, searchTerm)}
              disabled={pagination.page === 1 || loading}
            >
              ‹ Back
            </Button>
            <div className="flex h-9 items-center rounded-md border bg-muted/50 px-4 text-sm font-medium">
              Page {pagination.page} of {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchRows(pagination.page + 1, searchTerm)}
              disabled={pagination.page >= totalPages || loading}
            >
              Next ›
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchRows(totalPages, searchTerm)}
              disabled={pagination.page >= totalPages || loading}
            >
              Last »
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add site visit</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label htmlFor="add-path">Path</Label>
              <Input
                id="add-path"
                value={form.path}
                onChange={e => setForm(f => ({ ...f, path: e.target.value }))}
                placeholder="/books/…"
              />
            </div>
            <div>
              <Label htmlFor="add-referrer">Referrer</Label>
              <Textarea
                id="add-referrer"
                rows={2}
                value={form.referrer}
                onChange={e =>
                  setForm(f => ({ ...f, referrer: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="add-ua">User agent hash</Label>
              <Input
                id="add-ua"
                className="font-mono text-xs"
                value={form.user_agent_hash}
                onChange={e =>
                  setForm(f => ({ ...f, user_agent_hash: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="add-ip">IP address</Label>
              <Input
                id="add-ip"
                value={form.ip_address}
                onChange={e =>
                  setForm(f => ({ ...f, ip_address: e.target.value }))
                }
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="add-city">City</Label>
                <Input
                  id="add-city"
                  value={form.city}
                  onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="add-state">State</Label>
                <Input
                  id="add-state"
                  value={form.state}
                  onChange={e =>
                    setForm(f => ({ ...f, state: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="add-country">Country</Label>
                <Input
                  id="add-country"
                  value={form.country}
                  onChange={e =>
                    setForm(f => ({ ...f, country: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="add-browser-name">Browser name</Label>
                <Input
                  id="add-browser-name"
                  value={form.browser_name}
                  onChange={e =>
                    setForm(f => ({ ...f, browser_name: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="add-browser-ver">Browser version</Label>
                <Input
                  id="add-browser-ver"
                  value={form.browser_version}
                  onChange={e =>
                    setForm(f => ({ ...f, browser_version: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="add-os-name">OS name</Label>
                <Input
                  id="add-os-name"
                  value={form.os_name}
                  onChange={e =>
                    setForm(f => ({ ...f, os_name: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="add-os-ver">OS version</Label>
                <Input
                  id="add-os-ver"
                  value={form.os_version}
                  onChange={e =>
                    setForm(f => ({ ...f, os_version: e.target.value }))
                  }
                />
              </div>
            </div>
            <div>
              <Label htmlFor="add-device-type">Device type</Label>
              <Input
                id="add-device-type"
                placeholder="desktop, mobile, tablet, bot, other"
                value={form.device_type}
                onChange={e =>
                  setForm(f => ({ ...f, device_type: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="add-user-agent">User agent (raw)</Label>
              <Textarea
                id="add-user-agent"
                rows={3}
                className="font-mono text-xs"
                value={form.user_agent}
                onChange={e =>
                  setForm(f => ({ ...f, user_agent: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit site visit #{selected?.id}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Created at:{" "}
            {selected ? new Date(selected.created_at).toLocaleString() : "—"}{" "}
            (read-only)
          </p>
          <div className="grid gap-3 py-2">
            <div>
              <Label htmlFor="edit-path">Path</Label>
              <Input
                id="edit-path"
                value={form.path}
                onChange={e => setForm(f => ({ ...f, path: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="edit-referrer">Referrer</Label>
              <Textarea
                id="edit-referrer"
                rows={2}
                value={form.referrer}
                onChange={e =>
                  setForm(f => ({ ...f, referrer: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="edit-ua">User agent hash</Label>
              <Input
                id="edit-ua"
                className="font-mono text-xs"
                value={form.user_agent_hash}
                onChange={e =>
                  setForm(f => ({ ...f, user_agent_hash: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="edit-ip">IP address</Label>
              <Input
                id="edit-ip"
                value={form.ip_address}
                onChange={e =>
                  setForm(f => ({ ...f, ip_address: e.target.value }))
                }
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="edit-city">City</Label>
                <Input
                  id="edit-city"
                  value={form.city}
                  onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="edit-state">State</Label>
                <Input
                  id="edit-state"
                  value={form.state}
                  onChange={e =>
                    setForm(f => ({ ...f, state: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="edit-country">Country</Label>
                <Input
                  id="edit-country"
                  value={form.country}
                  onChange={e =>
                    setForm(f => ({ ...f, country: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="edit-browser-name">Browser name</Label>
                <Input
                  id="edit-browser-name"
                  value={form.browser_name}
                  onChange={e =>
                    setForm(f => ({ ...f, browser_name: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="edit-browser-ver">Browser version</Label>
                <Input
                  id="edit-browser-ver"
                  value={form.browser_version}
                  onChange={e =>
                    setForm(f => ({ ...f, browser_version: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="edit-os-name">OS name</Label>
                <Input
                  id="edit-os-name"
                  value={form.os_name}
                  onChange={e =>
                    setForm(f => ({ ...f, os_name: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="edit-os-ver">OS version</Label>
                <Input
                  id="edit-os-ver"
                  value={form.os_version}
                  onChange={e =>
                    setForm(f => ({ ...f, os_version: e.target.value }))
                  }
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit-device-type">Device type</Label>
              <Input
                id="edit-device-type"
                placeholder="desktop, mobile, tablet, bot, other"
                value={form.device_type}
                onChange={e =>
                  setForm(f => ({ ...f, device_type: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="edit-user-agent">User agent (raw)</Label>
              <Textarea
                id="edit-user-agent"
                rows={3}
                className="font-mono text-xs"
                value={form.user_agent}
                onChange={e =>
                  setForm(f => ({ ...f, user_agent: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this visit record?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes row #{selected?.id} from analytics. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default UserSiteVisits;
